import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { useAgentsMap } from "../../common/hooks/useAgents";
import type { Agent } from "../../common/types/Agent";
import {
    type FilterByMentionStatus,
    MentionStatusList,
    MentionStatusTextMap,
} from "../../common/types/Mention";
import type { SortSettings } from "../../common/types/Sort";
import {
    type FilterByAssignee,
    getActiveAssigneeSelections,
    getInactiveAssigneeSelections,
} from "../../common/utils/agents.util";
import {
    type UrlState,
    useComponentState,
    useTasksUrlParams,
} from "../../utils/customHook.util";
import { formatTotal } from "../../utils/format.util";
import { isMobile } from "../../utils/window.util";
import InputSearch from "../Common/Input.Search";
import Label from "../Common/Label";
import { List } from "../Common/List";
import ListFilter, { type ListFilterItem } from "../Common/ListFilter";
import PaginatedList from "../Common/list/PaginatedList";
import SortItemsBy from "../Common/SortItemsBy";
import Tag from "../Common/Tag";
import TaskCard from "./TaskCard";
import "./TasksListView.css";
import { useApiClient } from "@albertine/api-client";
import type {
    SortDto,
    TaskDto,
    TasksQueryFilterDto,
    TasksSearchResultDto,
    TasksSortFields,
} from "@albertine/types";
import { Lib } from "albertine-shared-web";
import { onlyUnique } from "../../common/utils/array.util";
import ListFilterGroup from "../Common/ListFilterGroup";

function getDefaultSortSettings(
    current: SortSettings,
    upcoming: SortSettings,
): SortSettings {
    const sortByChanged = current.by !== upcoming.by;

    if (sortByChanged) {
        const defaultOrder = upcoming.by === "mentionDueDate" ? "asc" : "desc";
        return {
            by: upcoming.by,
            order: defaultOrder,
        };
    }

    return upcoming;
}

async function queryTasksApi(
    apiClient: ReturnType<typeof useApiClient>["apiClient"],
    filter: TasksQueryFilterDto,
): Promise<TasksSearchResultDto> {
    try {
        const response = await apiClient.get.listTasks({
            query: {
                sortBy: filter.sort.by,
                sortOrder: filter.sort.order,
                page: String(filter.pagination?.from ?? 0),
                size: String(filter.pagination?.size ?? 100),
                searchTerm: filter.searchTerm,
                mentionStatus: filter.mentionStatus ?? [],
                assignedToAgentIds: filter.assignedToAgentIds ?? [],
            },
        });

        const json = await response.json();
        if (!json.success) {
            return { total: 0, tasks: [] };
        }

        return {
            total: json.data.pagination.total,
            tasks: json.data.tasks,
        };
    } catch (error) {
        Sentry.captureException(error, {
            extra: { message: "Failed to list tasks via API" },
        });
    }

    return { total: 0, tasks: [] };
}

type State = {
    total: number;
};

function TasksListView() {
    const { state, setState } = useComponentState<State>({
        total: 0,
    });
    const agentsMap = useAgentsMap();
    const { urlState, setUrlState } = useTasksUrlParams();
    const updateUrlState = (update: Partial<UrlState>) => {
        setUrlState((previousUrlState) => ({
            ...previousUrlState,
            ...update,
        }));
    };
    const [searchTerm, setSearchTerm] = useState<string>("");

    const assignees: Agent[] = Object.values(agentsMap);
    const statusFilterOptions: ListFilterItem<FilterByMentionStatus>[] =
        MentionStatusList.map((status) => ({
            data: {
                id: status,
                name: MentionStatusTextMap[status] || status,
            },
            selected: (urlState.mentionStatus || []).includes(status),
        }));

    const sortSettings = useMemo<SortDto<TasksSortFields>>(
        () => ({
            by: urlState.sort?.by ?? "mentionCreatedAt",
            order: urlState.sort?.order ?? "desc",
        }),
        [urlState.sort],
    );

    const { apiClient } = useApiClient();
    const PaginatedResults = useCallback(
        (paginatedProps: { filter: TasksQueryFilterDto }) => {
            const { filter } = paginatedProps;
            return (
                <PaginatedList<number, TaskDto>
                    className="tasks-list-view__results"
                    pageLength={100}
                    queryKey={["tasks", filter]}
                    queryForItems={async ({ size, page }) => {
                        const searchResult = await queryTasksApi(apiClient, {
                            sort: sortSettings,
                            pagination: {
                                from: page,
                                size,
                            },
                            mentionStatus: urlState.mentionStatus,
                            searchTerm: searchTerm,
                            assignedToAgentIds: urlState.assignedToAgentIds,
                        });

                        setState({ total: searchResult.total });

                        return { data: searchResult.tasks, total: searchResult.total };
                    }}
                    renderItem={(task) => (
                        <TaskCard
                            key={task.id}
                            task={task}
                            layout={isMobile() ? "vertical" : "horizontal"}
                        />
                    )}
                    renderError={() => <div>Error happened</div>}
                />
            );
        },

        [apiClient, sortSettings, urlState, searchTerm, setState],
    );

    return (
        <List.Tab className={classNames("tasks-list")}>
            <List.TabHeader className="tasks-list__header">
                <Lib.Flex.Row gap="value24">
                    <div className="tasks-list-view__title">
                        <Lib.Flex.Row gap="value8">
                            <h3>Tasks</h3>
                            <Tag.Medium.N90>{formatTotal(state.total)}</Tag.Medium.N90>
                        </Lib.Flex.Row>
                    </div>
                    <Lib.Flex.Row>
                        <InputSearch
                            placeholder="Search by message, member or request"
                            throttle={200}
                            onChange={setSearchTerm}
                        />
                    </Lib.Flex.Row>
                </Lib.Flex.Row>
            </List.TabHeader>

            <List.Header>
                <List.HeaderColumn>
                    <div>
                        <SortItemsBy
                            className="tasks-list__sort-by"
                            entries={[
                                ["mentionDueDate", "Sort by due date"],
                                ["mentionCreatedAt", "Sort by creation date"],
                                ["ticketNumber", "Sort by ticket number"],
                            ]}
                            initialSortOrder={sortSettings.order}
                            initialSortBy={sortSettings.by}
                            onChange={(by, order) => {
                                const newSettings = getDefaultSortSettings(
                                    sortSettings,
                                    { by, order },
                                );

                                setUrlState({
                                    sortBy: newSettings.by,
                                    sortOrder: newSettings.order,
                                });
                            }}
                        />
                    </div>
                </List.HeaderColumn>
                <List.HeaderColumn>
                    <Label.Medium.SemiBold.N10>
                        <ListFilter<FilterByMentionStatus>
                            title="Status"
                            items={statusFilterOptions}
                            onChange={(newStatusList) => {
                                const newStatuses = newStatusList.map((it) => it.id);
                                setUrlState({ mentionStatus: newStatuses });
                            }}
                            position="bottom-right"
                        />
                    </Label.Medium.SemiBold.N10>
                </List.HeaderColumn>
                <List.HeaderColumn>
                    <ListFilterGroup<FilterByAssignee>
                        title="Assigned to"
                        showAllFiltersWithoutCount
                        filters={[
                            {
                                title: "Active agents",
                                items: assignees
                                    .filter((agent) => !agent.disabled)
                                    .map((agent) => ({
                                        data: agent,
                                        selected: (
                                            urlState.assignedToAgentIds ?? []
                                        ).includes(agent.id),
                                        count: 0,
                                    })),
                                onChange: (newAgentList) => {
                                    const newAgentIds = newAgentList.map((it) => it.id);
                                    updateUrlState({
                                        assignedTo: [
                                            ...getInactiveAssigneeSelections(
                                                assignees,
                                                urlState.assignedToAgentIds ?? [],
                                            ),
                                            ...newAgentIds,
                                        ].filter(onlyUnique),
                                    });
                                },
                            },
                            {
                                title: "Inactive agents",
                                items: assignees
                                    .filter((agent) => agent.disabled)
                                    .map((agent) => ({
                                        data: agent,
                                        selected: (
                                            urlState.assignedToAgentIds ?? []
                                        ).includes(agent.id),
                                        count: 0,
                                    })),
                                onChange: (newAgentList) => {
                                    const newAgentIds = newAgentList.map((it) => it.id);
                                    updateUrlState({
                                        assignedTo: [
                                            ...getActiveAssigneeSelections(
                                                assignees,
                                                urlState.assignedToAgentIds ?? [],
                                            ),
                                            ...newAgentIds,
                                        ].filter(onlyUnique),
                                    });
                                },
                            },
                        ]}
                        position="bottom-right"
                    />
                </List.HeaderColumn>
            </List.Header>

            <PaginatedResults
                filter={{
                    searchTerm,
                    assignedToAgentIds: urlState.assignedToAgentIds,
                    mentionStatus: urlState.mentionStatus,
                    sort: urlState.sort,
                }}
            />
        </List.Tab>
    );
}

export default TasksListView;
